import {
  AutocompleteItem,
  AutocompleteSection,
  Autocomplete as NAutocomplete,
  type AutocompleteProps as Props,
} from '@heroui/react';
import type { Key } from 'react';
import { type Control, Controller, type FieldValues, type Path, type PathValue } from 'react-hook-form';
import { BiCheck } from 'react-icons/bi';
import { isEmpty } from '@/utils';
import { cn } from '../../cn';
import { ChipGroup, toaster } from '../../index';

interface ISelectItem {
  label: string;
  key: string | number;
  description?: string | null;
}

interface ISelectItemSection {
  label: string;
  items: ISelectItem[];
}

type AutoCompleteProps = Omit<Props, 'children'> & {
  options: ISelectItem[];
  sections?: ISelectItemSection[];
  children?: Props['children'];
  selectedKeys?: Key[];
  renderItem?: (item: ISelectItem) => React.ReactNode;
};

export const AutoCompleteMulti = ({
  options,
  sections,
  isVirtualized,
  children,
  className,
  selectedKeys,
  renderItem,
  ...props
}: AutoCompleteProps) => {
  const nested = sections
    ? sections.map((s) => (
        <AutocompleteSection key={s.label} showDivider title={s.label}>
          {s.items.map((o) => (
            <AutocompleteItem
              textValue={o.label}
              endContent={selectedKeys?.includes(o.key.toString()) && <BiCheck className='size-4' />}
              className='text-default-700 capitalize'
              key={o.key}
            >
              {renderItem ? (
                renderItem(o)
              ) : (
                <>
                  [{o.key}] {o.label}
                </>

                // <div className='flex flex-col space-y-1'>
                //   <span className='text-small capitalize'>{o.label}</span>
                //   {o.description && <span className='text-tiny text-default-400'>{o.description}</span>}
                // </div>
              )}
            </AutocompleteItem>
          ))}
        </AutocompleteSection>
      ))
    : options.map((o) => (
        <AutocompleteItem
          endContent={selectedKeys?.includes(o.key.toString()) && <BiCheck className='size-4' />}
          textValue={o.label}
          className='text-default-700 capitalize'
          key={o.key}
        >
          {renderItem ? (
            renderItem(o)
          ) : (
            <>
              [{o.key}] {o.label}
            </>
          )}
        </AutocompleteItem>
      ));
  return (
    <NAutocomplete
      isVirtualized={isVirtualized}
      className={cn('placeholder:text-default-400', className)}
      popoverProps={{
        classNames: { content: 'rounded-md' },
      }}
      classNames={{}}
      radius='sm'
      {...props}
    >
      {isVirtualized
        ? options.map((o) => (
            <AutocompleteItem textValue={o.label} className='text-default-700 capitalize' key={o.key}>
              {o.label}
            </AutocompleteItem>
          ))
        : children || nested}
    </NAutocomplete>
  );
};

export interface FormAutoCompleteMultiProps<T extends FieldValues>
  extends Omit<AutoCompleteProps, 'onValueChange'> {
  name: Path<T>;
  control: Control<T, unknown>;
  onValueChange?: (value: PathValue<T, Path<T>>) => void;
  transform?: (val: string) => PathValue<T, Path<T>>;
  showSelected?: boolean;
  transformSelected?: (val: string) => string;
  maxSelected?: number;
}

export const FormAutoCompleteMulti = <T extends FieldValues>({
  control,
  onValueChange,
  transform,
  name,
  showSelected = false,
  transformSelected,
  maxSelected,
  ...rest
}: FormAutoCompleteMultiProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      shouldUnregister
      render={({ field: { onChange, onBlur, value }, fieldState: { error, invalid, isDirty } }) => {
        const onHandleChange = (val: Key | null) => {
          let parseVal = val as PathValue<T, Path<T>>;
          let newVal: PathValue<T, Path<T>>[] = [];

          if (transform && !isEmpty(val)) {
            parseVal = transform(val as string) as never;
          }

          if (Array.isArray(value)) {
            if (value.includes(parseVal)) {
              newVal = value.filter((v: string) => v !== parseVal);
            } else {
              newVal = [...value, parseVal];
            }

            if (maxSelected && newVal.length > maxSelected) {
              toaster.warning(`You can only select up to ${maxSelected} items`);
              return;
            }
          } else {
            newVal = [parseVal];
          }

          if (onValueChange) {
            onValueChange(newVal as never);
          }
          onChange(newVal);
        };

        return (
          <AutoCompleteMulti
            {...rest}
            classNames={{
              base: '!overflow-visible',
              endContentWrapper: 'absolute top-[0.4px] right-3',
            }}
            startContent={
              <ChipGroup
                onRemove={onHandleChange}
                name={name}
                wrapperClassName=''
                transformSelected={transformSelected}
                options={value ? value : []}
                classNames={{
                  base: 'bg-background rounded-lg min-w-0 p-0.5',
                  content: 'truncate text-xs',
                }}
              />
            }
            inputProps={{
              classNames: {
                label:
                  'mt-1.5 !overflow-visible group-data-[filled-within=true]:!top-2 group-data-[filled-within=true]:!translate-y-0 group-data-[filled-within=true]:!mt-0',
                inputWrapper: cn('!overflow-visible', {
                  'min-h-10': value?.length === 0,
                  'h-auto pt-1': value?.length > 0,
                }),
                innerWrapper: cn('flex flex-wrap gap-1 h-auto max-w-[calc(100%-4rem)]', {
                  'mt-3 -ml-1.5': value?.length === 0,
                  'mt-8': value?.length > 0,
                }),
                input: 'w-20 h-7',
              },
            }}
            selectedKeys={value ? value : undefined}
            onBlur={onBlur}
            selectedKey={null}
            description={
              showSelected && value
                ? `${transformSelected ? value.map(transformSelected).join(', ') : value}`
                : undefined
            }
            onSelectionChange={onHandleChange}
            isInvalid={isDirty && invalid}
            errorMessage={error?.message}
          />
        );
      }}
    />
  );
};
